import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
// don't know why the linter complains about the following line but not about the line above?
// tslint:disable-next-line: max-line-length
import { CurrentNumberChanged } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-control-behaviour/call-control-behaviour.actions';
import { CallControlBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-control-behaviour/call-control-behaviour.state';
import { DashboardState } from 'src/app/store/dashboard/dashboard.state';
import { NextDialTargetChanged } from 'src/app/store/samwin/next-dial-target/next-dial-target.actions';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { AssistanceStateEnum } from 'src/app/store/samwin/samwin-user-assistance-status/assistance-state-enum';
import { SamwinUserAssistanceState } from 'src/app/store/samwin/samwin-user-assistance-status/samwin-user-assistance-status.state';
import { UserAssistanceStateRealTimeData } from 'src/generated/signalR/user-assistance-state-real-time-data';
import { CallControlSettingsState } from '../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/call-control-settings/call-control-settings.state';
import { AcceptOrStartCallAction, EndCallAction } from '../../../../store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from '../../../../store/samwin/client-phone/client-phone.state';
import { BasicBrickComponent } from '../../basic-brick-component';

@Component({ template: '' })
export abstract class CallControlBrickBaseComponent extends BasicBrickComponent implements OnInit, OnDestroy {
   @ViewChild('numberInDialpad', { static: false }) numberInDialpad: ElementRef;

   public currentNumber$: Observable<string>;

   @Select(SamwinLinesStateSelector.isCallInProgress)
   public isCallInProgress$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.canDisconnectLineA)
   public canDisconnectLineA$: Observable<boolean>;

   @Select(ClientPhoneState.canPhoneMakeCall)
   public canPhoneMakeCall$: Observable<boolean>;

   @Select(ClientPhoneState.isCallRingingAndAcceptable)
   public isCallRingingAndAcceptable$: Observable<boolean>;

   @Select(ClientPhoneState.getAcceptOrStartCallAction)
   public acceptOrStartCallAction$: Observable<'accept' | 'dial' | 'user-decision' | 'none'>;

   @Select(SamwinLinesStateSelector.isPhoneRinging)
   public isPhoneRinging$: Observable<boolean>;

   @Select(SamwinUserAssistanceState.getMyAssistedState)
   public myAssistedState$: Observable<UserAssistanceStateRealTimeData>;

   @Select(SamwinUserAssistanceState.getMyAssistedStateEnum)
   public myAssistedStateEnum$: Observable<AssistanceStateEnum>;

   public showDialpad$: Observable<boolean>;

   private destroy$ = new Subject();

   public brickInstanceId: string;

   public phoneHangUpIcon: IconDefinition = WellKnownIconsEnum.CALLING_HANGUP.icon;
   public phoneAnswerIcon: IconDefinition = WellKnownIconsEnum.CALLING_ANSWER.icon;
   public dialArrowIcon: IconDefinition = WellKnownIconsEnum.CALLING_DIAL_ARROW.icon;
   public userDecisionIcon: IconDefinition = WellKnownIconsEnum.CALLING_USER_DECISION.icon;

   constructor(protected store: Store) {
      super();
   }

   ngOnInit() {
      this.showDialpad$ = this.store.select(CallControlSettingsState.getIsDialpadShown).pipe(map((fn) => fn(this.brickInstanceId)));

      this.currentNumber$ = this.store.select(CallControlBehaviourState.getCurrentNumberFn).pipe(map((fn) => fn(this.brickInstanceId)));

      this.store
         .select(DashboardState.isBrickFocussedFn)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            takeUntil(this.destroy$)
         )
         .subscribe((hasFocus) => {
            if (hasFocus) {
               // (re-)publish Next Dial Target on focus
               // to allow the use of the dial button for numbers that have been published
               // by other sources while the number input field is empty, only publish
               // if number input field is not empty (i.e. a current number is present);
               // but a non-empty number input field will always have precedence when using the dial button
               const currentNumber = this.store.selectSnapshot(CallControlBehaviourState.getCurrentNumberFn)(this.brickInstanceId);
               if (_.isEmpty(currentNumber)) {
                  return;
               }
               this.store.dispatch(new NextDialTargetChanged(currentNumber, this.brickInstanceId));
            }
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   dialPadNumberChanged(newNumber: string) {
      this.store.dispatch(new CurrentNumberChanged(newNumber, this.brickInstanceId));
      this.numberInDialpad.nativeElement.focus();
   }

   hangUpPhone() {
      this.store.dispatch(new EndCallAction());
   }

   acceptOrStartCall() {
      this.store.dispatch(new AcceptOrStartCallAction());
   }

   onNumberInputFocusIn(eventData: FocusEvent) {
      // force update of Next Dial Target with empty number if empty input field is focussed
      const currentNumber = this.store.selectSnapshot(CallControlBehaviourState.getCurrentNumberFn)(this.brickInstanceId);
      if (!_.isEmpty(currentNumber)) {
         return;
      }
      this.store.dispatch(new NextDialTargetChanged('', this.brickInstanceId));
   }
}
